import { Link } from 'react-router-dom';

import Seo from '../../components/ui/Seo';
import SectionHeading from '../../components/ui/SectionHeading';
import Reveal from '../../components/ui/Reveal';
import { ArrowRight, Check } from '../../components/ui/Icons';

import Hero from '../../components/home/Hero';
import ShopByCategory from '../../components/home/ShopByCategory';
import ExploreColours from '../../components/home/ExploreColours';
import InteriorPaints from '../../components/home/InteriorPaints';
import ExteriorPaints from '../../components/home/ExteriorPaints';
import WhyChooseVertex from '../../components/home/WhyChooseVertex';
import StatsBand from '../../components/home/StatsBand';
import Inspiration from '../../components/home/Inspiration';
import BrandTicker from '../../components/home/BrandTicker';
import ExploreByRoom from '../../components/home/ExploreByRoom';
import ColourOfSeason from '../../components/home/ColourOfSeason';
import PromoBanners from '../../components/home/PromoBanners';
import PaintingJourney from '../../components/home/PaintingJourney';
import LatestStories from '../../components/home/LatestStories';
import DealerBand from '../../components/home/DealerBand';
import MarmovellaFeature from '../../components/home/MarmovellaFeature';
import CustomerReviews from '../../components/home/CustomerReviews';
import FinalCTA from '../../components/home/FinalCTA';
import Form from '../Form/Form';
import { ctaImages } from '../../data/homeImages';

const CERTIFICATIONS = ['ISO quality standards', 'Environmental compliance', 'Safety certifications', 'Third-party verified'];

/**
 * Homepage composition.
 *
 * Dense, image-led composition: every section either sits on a photograph or
 * alternates with one, and copy is boxed over imagery rather than floating in
 * white space. Order: hero (+ quick actions) → ticker → categories → rooms →
 * colour of the season → interior → waterproofing / wood / metal banners →
 * exterior → colour explorer → painting journey → why Vertex → stats →
 * inspiration → certification → reviews → journal → dealer finder →
 * expert-advice form → closing CTA.
 */
const HomePage = () => (
  <div className="min-h-screen">
    <Seo description="Vertex Paints — premium interior and exterior emulsions, primers, wood and metal finishes and waterproofing, with 1,800+ shades tinted on demand. Manufactured in Khanna, Punjab since 2007." />

    <Hero />
    <BrandTicker />
    <ShopByCategory />
    <ExploreByRoom />
    <ColourOfSeason />
    <MarmovellaFeature />
    <InteriorPaints />
    <PromoBanners />
    <ExteriorPaints />
    <ExploreColours />
    <PaintingJourney />
    <WhyChooseVertex />
    <StatsBand />
    <Inspiration />

    {/* ---------------- Certification ---------------- */}
    <section className="vp-section bg-brand-surface">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden border border-brand-line bg-white p-3 shadow-card">
              <img src={ctaImages.certificate} alt="Vertex Paints quality certificate awarded to Vertex Paints and Coatings Industry" className="w-full h-auto rounded-xl" loading="lazy" />
            </div>
            <span className="absolute -top-4 -right-2 sm:-right-4 vp-chip vp-chip-gold px-4 py-2 text-sm shadow-gold">Manufacturing since 2007</span>
          </Reveal>

          <Reveal delay={1} className="order-1 lg:order-2">
            <SectionHeading
              eyebrow="Certification"
              title="Quality you can check, not just take our word for"
              lead="Our manufacturing is audited against recognised industry standards, so the tin you buy performs the way the data sheet says it will."
              size="md"
            />

            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {CERTIFICATIONS.map((c) => (
                <li key={c} className="flex items-center gap-3 text-sm text-brand-ink/85 bg-white border border-brand-line rounded-xl px-4 py-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                    <Check size={12} />
                  </span>
                  {c}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/AboutUs/OurStory#quality" className="vp-btn vp-btn-primary">
                Our quality story
                <ArrowRight size={16} />
              </Link>
              <Link to="/TDS" className="vp-btn vp-btn-outline">
                View technical data sheets
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <CustomerReviews />
    <LatestStories />
    <DealerBand />

    {/* Lead capture — routed through submitLead() */}
    <section className="vp-section bg-white">
      <div className="container">
        <Reveal>
          <Form />
        </Reveal>
      </div>
    </section>

    <FinalCTA />
  </div>
);

export default HomePage;
